import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    IconButton,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/api";
import en_ja from "../../translations/en_ja";

const AddBanner = ({
    dialogOpen,
    onClose,
    editMode = false,
    bannerData,
    onSubmit,
    setSnackbarType,
    setSnackbarOpen,
    setSnackbarMessage,
}) => {
    const [formData, setFormData] = useState({
        banner_title: "",
        description: "",
        banner_image: null,
        bannerPreview: "",
        destination_url: "",
        start_date: "",
        end_date: "",
        color_style: ["#005BAB", "#D0E8FE"], // Changed: removed name, only colors
        badge: "",
        services: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [initialFormData, setInitialFormData] = useState(null);

    // Prefill for edit mode
    useEffect(() => {
        if (editMode && bannerData) {
            const initialData = {
                banner_title: bannerData.banner_title || "",
                description: bannerData.description || "",
                banner_image: null,
                bannerPreview: bannerData.banner_image || "",
                destination_url: bannerData.destination_url || "",
                start_date: bannerData.start_date ? bannerData.start_date.split("T")[0] : "",
                end_date: bannerData.end_date ? bannerData.end_date.split("T")[0] : "",
                color_style: Array.isArray(bannerData.color_style)
                    ? bannerData.color_style.filter(item => item.startsWith('#'))
                    : ["#005BAB", "#D0E8FE"],
                badge: bannerData.badge || "",
                services: bannerData.services || "",
            };

            setFormData(initialData);
            setInitialFormData(initialData);
        }
    }, [editMode, bannerData]);

    // Validation
    const validateForm = () => {
        const newErrors = {};
        if (!formData.banner_title.trim())
            newErrors.banner_title = en_ja["Banner title is required"];
        if (!formData.destination_url.trim())
            newErrors.destination_url = en_ja["Destination URL is required"];
        if (!editMode && !formData.banner_image)
            newErrors.banner_image = en_ja["Banner image is required (PNG up to 2MB)"];
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Image Upload
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file && file.type === "image/png" && file.size <= 2 * 1024 * 1024) {
            setFormData((prev) => ({
                ...prev,
                banner_image: file,
                bannerPreview: URL.createObjectURL(file),
            }));
            setErrors((prev) => ({ ...prev, banner_image: "" }));
        } else {
            setErrors((prev) => ({
                ...prev,
                banner_image: en_ja["Only PNG images up to 2MB are allowed"],
            }));
        }
    };

    // Handle field change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    // Submit API call
    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);
        try {
            const form = new FormData();
            form.append("banner_title", formData.banner_title);
            form.append("description", formData.description);
            form.append("destination_url", formData.destination_url);
            form.append("start_date", formData.start_date);
            form.append("end_date", formData.end_date);
            form.append("color_style", JSON.stringify(formData.color_style)); // array as string
            form.append("badge", formData.badge);
            form.append("services", formData.services);

            if (formData.banner_image) {
                form.append("banner_image", formData.banner_image);
            }

            let response;
            if (editMode) {
                response = await api.put(
                    `/v1/admin/edit/banner/${bannerData?.id}`,
                    form,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
            } else {
                response = await api.post(`/v1/admin/create/banner`, form, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }

            if (response.status === 200 || response.status === 201) {
                setSnackbarMessage(
                    response?.data?.message ||
                    (editMode
                        ? en_ja["Banner updated successfully"]
                        : en_ja["Banner added successfully"])
                );
                setSnackbarType("success");
                setSnackbarOpen(true);
                if (onSubmit) onSubmit();
                handleClose();
            }
        } catch (error) {
            console.error("Error submitting banner:", error);
            setSnackbarMessage(
                error?.response?.data?.message || en_ja["Something went wrong."]
            );
            setSnackbarType("error");
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    // Reset and close
    const handleClose = () => {
        setFormData({
            banner_title: "",
            description: "",
            banner_image: null,
            bannerPreview: "",
            destination_url: "",
            start_date: "",
            end_date: "",
            color_style: ["#005BAB", "#D0E8FE"], // Changed: default colors only
            badge: "",
            services: "",
        });
        setErrors({});
        onClose();
    };

    const hasChanges = editMode && initialFormData
        ? (
            formData.banner_title !== initialFormData.banner_title ||
            formData.description !== initialFormData.description ||
            formData.destination_url !== initialFormData.destination_url ||
            formData.start_date !== initialFormData.start_date ||
            formData.end_date !== initialFormData.end_date ||
            formData.badge !== initialFormData.badge ||
            formData.services !== initialFormData.services ||
            formData.banner_image !== null || // new image uploaded
            formData.color_style[0] !== initialFormData.color_style[0] ||
            formData.color_style[1] !== initialFormData.color_style[1]
        )
        : true;

    // Helper function to compare color arrays
    const areColorsEqual = (colors1, colors2) => {
        return colors1[0] === colors2[0] && colors1[1] === colors2[1];
    };

    return (
        <Dialog open={dialogOpen} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {editMode ? en_ja["Edit Banner"] : en_ja["Add New Banner"]}
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ flex: 1, minWidth: 300 }}>
                        <TextField
                            label={en_ja["Banner Title"]}
                            name="banner_title"
                            fullWidth
                            value={formData.banner_title}
                            onChange={handleChange}
                            error={!!errors.banner_title}
                            helperText={errors.banner_title}
                            margin="normal"
                        />

                        <TextField
                            label={en_ja["Description"]}
                            name="description"
                            fullWidth
                            multiline
                            rows={2}
                            value={formData.description}
                            onChange={handleChange}
                            margin="normal"
                        />

                        {/* Image Upload */}
                        <Box
                            sx={{
                                border: "1px dashed gray",
                                borderRadius: 2,
                                p: 3,
                                textAlign: "center",
                                mt: 2,
                                mb: 1,
                            }}
                        >
                            <Typography variant="body2" color="textSecondary">
                                {en_ja["Drop image here or browse"]}
                            </Typography>
                            <Typography variant="caption">
                                {en_ja["Recommended: 800x400px, Max 2MB (PNG only)"]}
                            </Typography>
                            <br />
                            <input
                                type="file"
                                accept="image/png"
                                style={{ marginTop: 8 }}
                                onChange={handleImageUpload}
                            />
                            {errors.banner_image && (
                                <Typography color="error">{errors.banner_image}</Typography>
                            )}
                        </Box>

                        <TextField
                            label={en_ja["Destination URL"]}
                            name="destination_url"
                            fullWidth
                            value={formData.destination_url}
                            onChange={handleChange}
                            error={!!errors.destination_url}
                            helperText={errors.destination_url}
                            margin="normal"
                        />

                        <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField
                                type="date"
                                label={en_ja["Start Date"]}
                                name="start_date"
                                fullWidth
                                value={formData.start_date}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                            />
                            <TextField
                                type="date"
                                label={en_ja["End Date"]}
                                name="end_date"
                                fullWidth
                                value={formData.end_date}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                            />
                        </Box>

                        <TextField
                            label={en_ja["Badge"]}
                            name="badge"
                            fullWidth
                            value={formData.badge}
                            onChange={handleChange}
                            margin="normal"
                        />
                        <TextField
                            label={en_ja["Services"]}
                            name="services"
                            fullWidth
                            value={formData.services}
                            onChange={handleChange}
                            margin="normal"
                        />
                    </Box>

                    {/* Live Preview */}
                    <Box sx={{ flex: 1, minWidth: 300, mt: 2 }}>
                        <Box
                            sx={{
                                border: "1px solid #e0e0e0",
                                borderRadius: "16px",
                                p: 2,
                                backgroundColor: "#fff",
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                {en_ja["Live Preview"]}
                            </Typography>
                            <Box
                                sx={{
                                    borderRadius: "16px",
                                    overflow: "hidden",
                                    height: 250,
                                    width: "100%",
                                    background: `linear-gradient(90deg, ${formData.color_style[0]} 0%, ${formData.color_style[1]} 100%)`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    px: 4,
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        color: "white",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        height: "100%",
                                        py: 2,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            overflowY: "auto",
                                            maxHeight: "180px",
                                            "&::-webkit-scrollbar": { display: "none" },
                                            display: "flex",
                                            justifyContent: "center",
                                            flexDirection: "column"
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {formData.banner_title ||
                                                en_ja["Banner Title Placeholder"]}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ mt: 1, wordBreak: "break-word" }}
                                        >
                                            {formData.description ||
                                                en_ja["Banner description goes here"]}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                        {en_ja["All Services Available | T&C Applied"]}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        flex: 1,
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    {formData.bannerPreview ? (
                                        <Box
                                            component="img"
                                            src={formData.bannerPreview}
                                            alt="Preview"
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                                borderRadius: "12px",
                                            }}
                                        />
                                    ) : (
                                        <Typography variant="body2" color="white">
                                            {en_ja["No image selected"]}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Color Picker - Updated without names */}
                            <Box mt={4}>
                                <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                    {en_ja["Color Style"]}
                                </Typography>
                                <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                                    {[
                                        ["#005BAB", "#D0E8FE"], // royal blue
                                        ["#FE9C9F", "#FACEC3"], // blush pink
                                        ["#E26D00", "#FFC58F"], // orange
                                        ["#EF19CC", "#FFC2F5"], // pink
                                        ["#9DC214", "#F1FFBE"], // green
                                    ].map((colors) => (
                                        <Box
                                            key={colors.join('-')}
                                            onClick={() =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    color_style: colors,
                                                }))
                                            }
                                            sx={{
                                                width: 80,
                                                height: 50,
                                                borderRadius: "10px",
                                                cursor: "pointer",
                                                border: areColorsEqual(formData.color_style, colors)
                                                    ? "3px solid black"
                                                    : "1.5px solid #ccc",
                                                background: `linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "border 0.2s ease-in-out",
                                                position: "relative",
                                            }}
                                        >
                                            {areColorsEqual(formData.color_style, colors) && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="24"
                                                        height="24"
                                                        fill="none"
                                                        stroke="white"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleClose}
                    sx={{
                        backgroundColor: "rgba(162, 164, 167, 1)",
                        color: "white",
                        borderRadius: 1.5,
                        textTransform: "none",
                        fontWeight: "600",
                        px: 4,
                        py: 1,
                    }}
                >
                    {en_ja["Cancel"]}
                </Button>
                <Button
                    variant="contained"
                    disabled={
                        loading ||
                        (editMode && !hasChanges)
                    }
                    onClick={handleSubmit}
                    sx={{
                        backgroundColor: "rgba(220, 53, 69, 1)",
                        color: "white",
                        borderRadius: 1.5,
                        textTransform: "none",
                        fontWeight: "600",
                        px: 4,
                        py: 1,
                    }}
                >
                    {loading
                        ? en_ja["Saving..."]
                        : editMode
                            ? en_ja["Update Banner"]
                            : en_ja["Create"]}
                </Button>

            </DialogActions>
        </Dialog>
    );
};

export default AddBanner;